import sharedb from 'sharedb/lib/client'
import type { ShallowRef } from 'vue'
import { nextTick, onUnmounted, ref, shallowRef, watch } from 'vue'
import { fabric } from 'fabric'
import ReconnectingWebSocket from 'reconnecting-websocket'
import _debounce from 'lodash-es/debounce'
import { useI18n } from 'vue-i18n'
import type { ICanvasPanEvent, ICanvasZoomEvent } from '../services/whiteboard'
import type Whiteboard from '../services/whiteboard'
import type WbDynamic from '../services/dynamic'
import type WbArticleImage from '../services/articleImage'
import type WbDiscussionIcon from '../services/discussionIcon'
import type WbModelDetails from '../services/modelDetails'
import type WbModelImage from '../services/modelImage'
import appConfig from '@/services/appConfig'
import { useUserStore } from '@/store/userData'
import { useNotificationStore } from '@/store/notification'
import utils from '@/services/utils'
import { privileges, whiteboardConstants } from '@/models/constants'
import { getArticleAssets, getArticlesAssets } from '@/api/t1/article'
import type MyArticle from '@/models/myArticle'
import type DuneAsset from '@/models/duneAsset'
import { useWhiteboardStore } from '@/store/whiteboard'

interface IUserData {
  zoomFactor: number
  zoomPoint?: IPoint
  panViewport: number[]
}

export default function useCollaboration(whiteboardId: number, wb: ShallowRef<Whiteboard | undefined>) {
  const boardId = `${appConfig.T1Env}${whiteboardId}`
  const dbConn = shallowRef<sharedb.Connection>()
  const dbDoc = shallowRef<sharedb.Doc>()
  const userDoc = shallowRef<sharedb.Doc>()
  const { t } = useI18n()
  const userStore = useUserStore()
  const notificationStore = useNotificationStore()
  const whiteboardStore = useWhiteboardStore()
  const collaborationReady = ref(false)
  const connected = ref(false)
  let userData: IUserData = { zoomFactor: 1, panViewport: [] }
  let ws: ReconnectingWebSocket
  const abortController = new AbortController()

  watch(wb, () => {
    if (dbConn.value) {
      dbConn.value.close()
      dbConn.value = undefined
    }

    if (dbDoc.value) {
      dbDoc.value.destroy(() => register())
      dbDoc.value = undefined
    }
    else {
      register()
    }
  }, { immediate: true })

  onUnmounted(() => {
    if (dbDoc.value) { dbDoc.value.destroy() }
    if (userDoc.value) { userDoc.value.destroy() }
    if (ws) { ws.close(4999, 'Unmounted') }
    abortController.abort('User Cancelled')
  })

  function register() {
    if (!wb.value) { return }
    collaborationReady.value = false

    // Create WebSocket
    console.log('Registering', appConfig)
    ws = new ReconnectingWebSocket(`${appConfig.ShareDbUrl}/server?boardId=${boardId}&t=${encodeURIComponent(localStorage.getItem('tk') || '')}`)
    ws.addEventListener('open', () => {
      connected.value = true
      console.log('Connected to WS')
    })
    ws.addEventListener('close', (e) => {
      connected.value = false
      console.log('Disconnected from WS', e)
    })

    // Create connection
    // eslint-disable-next-line ts/ban-ts-comment
    // @ts-expect-error
    dbConn.value = new sharedb.Connection(ws)
    dbDoc.value = dbConn.value.get('boards', boardId)
    // Subscribe to changes in the document
    dbDoc.value.subscribe(async (err) => {
      if (err) {
        console.error(err)
        return
      }
      console.log('initializing Board')
      // if document has been fetch but type is not set it means document has not been created yet https://share.github.io/sharedb/api/doc#type--type
      if (!dbDoc.value?.type) {
        // Document does not exist, create it
        dbDoc.value?.create({ version: 0 })
        collaborationReady.value = true
      }
      else {
        // Load initial objects
        const objectOrder = new Set<string>()
        if (dbDoc.value.data?.objectOrder && dbDoc.value.data.objectOrder.length) {
          dbDoc.value.data.objectOrder.forEach(id => objectOrder.add(id))
        }
        else {
          for (const key in dbDoc.value.data) {
            if (dbDoc.value.data[key].type === whiteboardConstants.objectTypes.frame) {
              objectOrder.add(key)
            }
          }
        }

        whiteboardStore.setSettings(dbDoc.value.data?.settings)

        for (const key in dbDoc.value.data) {
          if (!whiteboardConstants.excludeKeys.includes(key)) {
            objectOrder.add(key)
          }
        }

        const linkedCatalogCodes = new Set<number>()
        const articleIds = new Set<number>()
        const modelNumbers = new Set<string>()
        const modelIds = new Set<number>()

        const articleObjsMap = new Map<string, any[]>()
        const modelObjsMap = new Map<string, any[]>()
        const modelIdObjsMap = new Map<string, any[]>()

        const objs: any[] = []
        const groups: any[] = []

        function updateArticleObjsMap(obj) {
          if (!obj.isRequest && (obj.type === whiteboardConstants.objectTypes.articleImage
            || obj.type === whiteboardConstants.objectTypes.articleDetails
            || obj.type === whiteboardConstants.objectTypes.modelImage
            || obj.type === whiteboardConstants.objectTypes.modelDetails
            || obj.type === whiteboardConstants.objectTypes.designOption
          )) {
            if (obj.catalogCode && obj.modelId) {
              const mapKey = `${obj.catalogCode}-${obj.modelId}`
              if (!modelIdObjsMap.has(mapKey)) {
                modelIdObjsMap.set(mapKey, [])
              }
              modelIdObjsMap.get(mapKey)!.push(obj)

              modelIds.add(obj.modelId)
            }
            else if (obj.catalogCode && obj.modelNumber) {
              const mapKey = `${obj.catalogCode}-${obj.modelNumber}`
              if (!modelObjsMap.has(mapKey)) {
                modelObjsMap.set(mapKey, [])
              }
              modelObjsMap.get(mapKey)!.push(obj)

              modelNumbers.add(obj.modelNumber)
            }
            else if (obj.catalogCode && obj.articleId) {
              const mapKey = `${obj.catalogCode}-${obj.articleId}`
              if (!articleObjsMap.has(mapKey)) {
                articleObjsMap.set(mapKey, [])
              }
              articleObjsMap.get(mapKey)!.push(obj)

              articleIds.add(obj.articleId)
            }
            else if (userStore.activeCatalog && obj.articleId) {
              const mapKey = `${userStore.activeCatalog.CatalogCode}-${obj.articleId}`
              if (!articleObjsMap.has(mapKey)) {
                articleObjsMap.set(mapKey, [])
              }
              articleObjsMap.get(mapKey)!.push(obj)

              articleIds.add(obj.articleId)
            }
            if (userStore.activeCatalog && obj.catalogCode && obj.catalogCode !== userStore.activeCatalog?.CatalogCode) {
              linkedCatalogCodes.add(obj.catalogCode)
            }
          }
        }

        for (const key of objectOrder) {
          if (dbDoc.value.data[key]) {
            const obj = dbDoc.value.data[key]
            if (obj.type === whiteboardConstants.objectTypes.group) {
              groups.push(obj)
              if (obj.objects?.length) {
                obj.objects.forEach(o => updateArticleObjsMap(o))
              }
              continue
            }
            else {
              updateArticleObjsMap(obj)
            }
            objs.push(obj)
          }
        }

        let isApiFailed = false
        if (appConfig.DB && userStore.activeCatalog && userStore.myAttributes) {
          const allArticles: MyArticle[] = []
          const promises: Promise<any>[] = []
          if (modelNumbers.size > 0) {
            promises.push(appConfig.DB.getFirstActiveMyArticleByModelNumbers([...modelNumbers], userStore.activeCatalog, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, [...linkedCatalogCodes], false, abortController.signal)
              .then((myArticles) => {
                myArticles.forEach((article) => {
                  const articleMapKey = `${article.CatalogCode}-${article.Id}`
                  const mapKey = `${article.CatalogCode}-${article.ModelNumber}`
                  if (modelObjsMap.has(mapKey)) {
                    modelObjsMap.get(mapKey)!.forEach((obj) => {
                      obj.modelId = article.ModelId
                      obj.modelNumber = article.ModelNumber
                      obj.articleId = article.Id
                      obj.article = article
                      if (!articleObjsMap.has(articleMapKey)) {
                        articleObjsMap.set(articleMapKey, [])
                      }
                      articleObjsMap.get(articleMapKey)!.push(obj)
                    })
                  }
                  allArticles.push(article)
                })
              }))
          }
          if (modelIds.size > 0) {
            promises.push(appConfig.DB.getFirstActiveMyArticleByModelIds([...modelIds], userStore.activeCatalog, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, [...linkedCatalogCodes], false, abortController.signal)
              .then((myArticles) => {
                myArticles.forEach((article) => {
                  const articleMapKey = `${article.CatalogCode}-${article.Id}`
                  const mapKey = `${article.CatalogCode}-${article.ModelId}`
                  if (modelIdObjsMap.has(mapKey)) {
                    modelIdObjsMap.get(mapKey)!.forEach((obj) => {
                      obj.modelId = article.ModelId
                      obj.articleId = article.Id
                      obj.modelNumber = article.ModelNumber
                      obj.article = article
                      if (!articleObjsMap.has(articleMapKey)) {
                        articleObjsMap.set(articleMapKey, [])
                      }
                      articleObjsMap.get(articleMapKey)!.push(obj)
                    })
                  }
                  allArticles.push(article)
                })
              }))
          }
          if (articleIds.size > 0) {
            promises.push(appConfig.DB.getMyArticlesByIds(userStore.activeCatalog, userStore.linkedCatalogDetails, userStore.myAttributes, userStore.currentUsername, userStore.userProfile.Id, [...articleIds], userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, [...linkedCatalogCodes], userStore.currentCustomerSegmentations, true, abortController.signal)
              .then((myArticles) => {
                myArticles.forEach((article) => {
                  const mapKey = `${article.CatalogCode}-${article.Id}`
                  if (articleObjsMap.has(mapKey)) {
                    articleObjsMap.get(mapKey)!.forEach((obj) => {
                      obj.article = article
                    })
                  }
                  allArticles.push(article)
                })
              }))
          }
          if (promises.length) {
            await Promise.all(promises)
              .catch((e) => {
                isApiFailed = true
                notificationStore.addNotification({ message: t('general.unexpectedError'), type: 'Alert', actions: ['Support', 'ShowDetails'], details: utils.getErrorMessage(e) })
                console.error(e)
              })

            if (userStore.activeCatalog && allArticles.length) {
              promises.length = 0

              // load assets
              const articlesByNumber: Record<string, MyArticle[]> = {}
              const imageSets: string[] = []

              for (const obj of objs) {
                if (!obj.article) { continue }

                if (obj.type === whiteboardConstants.objectTypes.articleImage) {
                  if (!articlesByNumber.hasOwnProperty(obj.article.ArticleNumber)) {
                    articlesByNumber[obj.article.ArticleNumber] = []
                    imageSets.push(obj.article.ArticleNumber)
                  }
                  articlesByNumber[obj.article.ArticleNumber].push(obj.article)
                }
                else if (obj.type === whiteboardConstants.objectTypes.modelImage) {
                  if (!articlesByNumber.hasOwnProperty(obj.article.ModelNumber)) {
                    articlesByNumber[obj.article.ModelNumber] = []
                    imageSets.push(obj.article.ModelNumber)
                  }
                  articlesByNumber[obj.article.ModelNumber].push(obj.article)
                }
              }

              const newestImageAssetKeySet = new Set<string>((userStore.activeCatalog.Config.NewestImageAssetKeyList || []).map(x => x.toLowerCase()))
              if (imageSets.length) {
                promises.push(getArticlesAssets(userStore.activeCatalog.DuneContext, userStore.activeCatalog.ContextKey, imageSets)
                  .then((resp) => {
                    if (resp && resp.data && resp.data.length) {
                      const imageSetAssets = resp.data.reduce<Record<string, DuneAsset[]>>((acc, asset) => {
                        const key = asset.ImageSet
                        if (!acc[key]) { acc[key] = [] }
                        acc[key].push(asset)
                        return acc
                      }, {})
                      for (const imageSet in imageSetAssets) {
                        let assets = imageSetAssets[imageSet]
                        assets = utils.sortAssets(assets, newestImageAssetKeySet)
                        if (articlesByNumber.hasOwnProperty(imageSet)) {
                          articlesByNumber[imageSet].forEach((article) => {
                            article._Assets = assets
                            article._isAssetsLoaded = true
                          })
                        }
                      }
                      for (const number in articlesByNumber) {
                        if (!imageSetAssets.hasOwnProperty(number)) {
                          articlesByNumber[number].forEach((article) => {
                            article._isAssetsLoaded = true
                          })
                        }
                      }
                    }
                  }))
              }

              // load sku counts
              promises.push(utils.loadSkuCounts(userStore.activeCatalog, allArticles))

              await Promise.all(promises)
                .catch((e) => {
                  console.error('Unable to load assets/sku', e)
                })
            }
          }
        }

        if (!isApiFailed) {
          const objectPromises = wb.value?.addObjectsFromJson(objs).then(() => {
            wb.value?.performMoveToObject()
          })

          const groupPromises = groups.map((group) => {
            if (group.objects && group.objects.length > 0) {
              return wb.value?.addGroupFromJson(group).then(() => {
                wb.value?.performMoveToObject()
              })
            }
            return Promise.resolve()
          })

          Promise.all([objectPromises, ...groupPromises])
            .then(() => {
              collaborationReady.value = true
              wb.value?.onCanavasObjectsLoaded(wb.value)
            })
            .catch((error) => {
              console.error('Error loading canvas objects:', error)
            })
        }
      }
    })

    userDoc.value = dbConn.value.get('usersData', userStore.userProfile.UserName)
    userDoc.value.subscribe((err) => {
      if (err) {
        console.error(err)
        return
      }
      if (!userDoc.value?.type) {
        // Document does not exist, create it
        userDoc.value?.create({ version: 0 })
      }
      else if (utils.isDefined(userDoc.value?.data) && utils.isDefined(userDoc.value?.data[boardId])) {
        userData = userDoc.value.data[boardId]
        if (userData.panViewport.length) {
          wb.value?.canvas.setViewportTransform(userData.panViewport)
        }
        wb.value?.zoom(userData.zoomFactor, userData.zoomPoint)
      }
    })

    // Handle operations from shareDB
    dbDoc.value.on('op', async (op, source) => {
      console.log('new op', op, source)
      if (source || !op || !op[0] || !op[0].p || !op[0].p[0]) { return }

      const objs: any = wb.value?.canvas.getObjects()

      if (op[0].p[0] === 'objectOrder') {
        const newOrder = op[0].oi
        if (newOrder && newOrder.length) {
          newOrder.forEach((id: string) => {
            const obj = objs.find(obj => obj.id === id)
            if (obj) {
              obj.bringToFront()
            }
          })

          wb.value!.reorderObjects()
          wb.value!.canvas.requestRenderAll()
        }
      }
      else if (op[0].p[0] === 'settings') {
        whiteboardStore.setSettings(op[0].oi)
      }
      else {
        const obj = objs?.find(o => o.id === op[0].p[0])

        if (op[0].hasOwnProperty('oi')) {
          // Operation is to insert/update object
          if (obj) {
            // Set Current Lock
            const currentLock = obj.lock

            // Object exists, update
            const configuredImageKeys = (userStore.activeCatalog?.Config.WhiteboardImagePlaceholderOptions?.keys || []).map(key => key.toLowerCase())
            if (obj.type === whiteboardConstants.objectTypes.articleImage && op[0].oi.assetKey && configuredImageKeys.includes(op[0].oi.assetKey.toLowerCase()) && (userStore.userProfile.isValidPrivilege(privileges.article.allowBlockedArticleImages) || obj.BlockImage === 0)) {
              const catalogDetails = userStore.linkedCatalogDetails[obj.catalogCode] || userStore.activeCatalog
              const assets = await getArticleAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, obj.articleNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList)
              obj.assets = assets
              obj.imgLoaded = false
              obj.imgLoading = false
            }
            if ((obj.type === whiteboardConstants.objectTypes.articleImage || obj.type === whiteboardConstants.objectTypes.modelImage || obj.type === whiteboardConstants.objectTypes.modelDetails)) {
              obj.setProp('showDesignOptions', { showDesignOptions: op[0].oi.showDesignOptions }, true, false)
              if (obj.type === whiteboardConstants.objectTypes.articleImage || obj.type === whiteboardConstants.objectTypes.modelImage) {
                if (op[0].oi.assetKey !== obj.assetKey) {
                  obj.setProp('imageType', { assetKey: op[0].oi.assetKey }, true, false)
                }
              }
            }
            else if (obj.type === whiteboardConstants.objectTypes.frame || obj.type === whiteboardConstants.objectTypes.dynamic) {
              nextTick(() => obj.repositionTitle())
            }
            else if (obj.type === whiteboardConstants.objectTypes.dunesImage) {
              nextTick(() => obj.applyScaling(false))
            }
            let applyFilter = false
            let applyTemplateOptions = false

            if (obj.type === whiteboardConstants.objectTypes.dynamic) {
              const dyn = obj as WbDynamic
              const currentFilter = JSON.stringify(dyn.filterJson)
              const currentTemplateOptions = JSON.stringify(dyn.templateOptions)

              if (currentTemplateOptions !== JSON.stringify(op[0].oi.templateOptions)) {
                applyTemplateOptions = true
                applyFilter = true
              }
              if (currentFilter !== JSON.stringify(op[0].oi.filterJson) || dyn.hideInactive !== op[0].oi.hideInactive || dyn.showLabels !== op[0].oi.showLabels) {
                applyFilter = true
              }
              dyn.set(op[0].oi)
              if (applyTemplateOptions) {
                dyn.reloadTemplate()
              }
              if (applyFilter) {
                dyn.applyFilter()
              }
            }
            const selObjs = wb.value!.canvas.getActiveObjects()
            if (selObjs && selObjs.length > 0 && selObjs.some((o: any) => o.id === obj.id)) {
              wb.value!.canvas.discardActiveObject()
              obj.set(op[0].oi)
              // It is causing objects to disappear
              // const sel = new fabric.ActiveSelection(selObjs, { canvas: wb.value!.canvas })
              // wb.value!.canvas.setActiveObject(sel)
            }
            else {
              obj.set(op[0].oi)
            }

            if (currentLock !== obj.lock) {
              obj.setLock(obj.lock)
            }

            obj.dirty = true
            wb.value!.canvas.requestRenderAll()

            if (obj.type === whiteboardConstants.objectTypes.articleImage || obj.type === whiteboardConstants.objectTypes.modelDetails || obj.type === whiteboardConstants.objectTypes.modelImage) {
              (obj as WbArticleImage | WbModelDetails | WbModelImage).onServerUpdate()
            }

            if (obj.type === whiteboardConstants.objectTypes.discussion) {
              (obj as WbDiscussionIcon).calcUniqueUsers()
            }
            wb.value!.emitObjectModifiedServer(obj)
          }
          else {
            // New object, create
            const newObject = op[0].oi
            console.warn('adding', newObject)

            if (newObject.catalogCode && newObject.articleId) {
              newObject.article = wb.value?.getArticleFromMap(newObject.catalogCode, newObject.articleId)
            }

            newObject.excludeFromExport = true
            wb.value?.addObjectsFromJson([newObject])
              .then(() => wb.value?.canvas.requestRenderAll())
              .finally(() => newObject.excludeFromExport = undefined)
          }
        }
        else if (op[0].hasOwnProperty('od')) {
          // Operation is to remove object
          if (obj) {
            console.warn('removing')
            wb.value?.canvas.remove(obj)
            wb.value?.canvas.requestRenderAll()
          }
        }
      }
    })

    // Handle events on the canvas
    // Object added
    wb.value.canvas.on('object:added', (e) => {
      try {
        wb.value?.addObjToArticleMap(e.target)
        const obj = e.target?.toObject()
        if (obj?.id && !e.target?.excludeFromExport && collaborationReady.value && !obj?.localOnly) {
          submitUpsertOp(obj)
        }
      }
      catch (error) {
        console.error('Error in object:added event:', e.target, error)
      }
    })

    // Object modified
    wb.value.canvas.on('object:modified', (e) => {
      if (e.target) {
        if (e.target.isType('activeSelection')) {
          // If it's a selection, then iterate thru all objects within
          const objs = wb.value!.canvas.getActiveObjects()
          wb.value!.canvas.discardActiveObject()
          const targetObjs = objs.filter((o: any) => o.id && !o.localOnly).map(o => o.toObject())
          if (targetObjs.length > 0 && collaborationReady.value) {
            submitBulkUpsertOp(targetObjs)
          }
          const sel = new fabric.ActiveSelection(objs, { canvas: wb.value!.canvas })
          sel.setObjectsCoords()
          sel.setCoords()
          wb.value!.canvas.setActiveObject(sel)
          wb.value!.canvas.requestRenderAll()
        }
        else {
          const activeObjects = wb.value!.canvas.getActiveObjects()
          if (!e.subTargets && activeObjects.length > 1) {
            wb.value!.canvas.discardActiveObject()
            const obj: any = e.target
            if (obj?.id && collaborationReady.value && !obj?.localOnly) {
              submitUpsertOp(obj)
            }

            const sel = new fabric.ActiveSelection(activeObjects, { canvas: wb.value!.canvas })
            sel.setObjectsCoords()
            sel.setCoords()
            wb.value!.canvas.setActiveObject(sel)
            wb.value!.canvas.requestRenderAll()
          }
          else {
            const obj = e.target.toObject()
            if (obj?.id && collaborationReady.value && !obj?.localOnly) {
              submitUpsertOp(obj)
            }
          }
        }
      }
    })

    // Object deleted
    wb.value.canvas.on('object:removed', (e) => {
      if (e.target) {
        wb.value?.removeObjectFromArticleMap(e.target)
        const obj = e.target.toObject()
        if (obj.id && collaborationReady.value && !obj?.localOnly) {
          console.log('object:removed', obj)
          submitDeleteOp(obj)
        }
      }
    })

    // Objects Order
    wb.value.on('object-order', () => {
      if (dbDoc.value && dbDoc.value.data && wb.value) {
        const objectOrder = wb.value.canvas.getObjects().filter(obj => !(obj as any).localOnly && obj.type !== whiteboardConstants.objectTypes.discussion && (obj as any).id?.length > 0).map((obj: any) => obj.id)
        submitUpsertOp(objectOrder, 'objectOrder')
      }
    })

    wb.value.on('update-settings', (e) => {
      whiteboardStore.setSettings(e.val)
      submitUpsertOp(e.val, 'settings')
    })

    wb.value.on('zoom', (e: ICanvasZoomEvent) => {
      userData.zoomFactor = e.factor
      userData.zoomPoint = e.point
      userDoc.value?.submitOp([{ p: [boardId], oi: userData }])
    })

    const panDebounced = _debounce((e: ICanvasPanEvent) => {
      userData.panViewport = e.viewport
      userDoc.value?.submitOp([{ p: [boardId], oi: userData }])
    }, 500)
    wb.value.on('pan', panDebounced)
  }

  function submitUpsertOp(obj: any, id?: string) {
    const objectId = id || obj.id
    if (dbDoc.value && dbDoc.value.data && obj && objectId && collaborationReady.value) {
      if (dbDoc.value.data[objectId]) {
        // adding new comments should update
        if (JSON.stringify(obj) === JSON.stringify(dbDoc.value?.data[objectId]) && obj.type !== whiteboardConstants.objectTypes.discussion) {
          console.log('object already exists with same data', objectId, dbDoc.value?.data[objectId])
          return
        }
        console.log('object already exists, updating', objectId, dbDoc.value.data[objectId])
        dbDoc.value.submitOp([{ p: [objectId], od: dbDoc.value.data[objectId], oi: obj }], {}, shareDBCallback)
      }
      else {
        console.log('object does not exist, adding', obj)
        dbDoc.value.submitOp([{ p: [objectId], oi: obj }], {}, shareDBCallback)
      }
    }
  }

  function submitBulkUpsertOp(objs: any[]) {
    if (dbDoc.value && dbDoc.value.data && objs && objs.length && collaborationReady.value) {
      const ops = objs.map((obj) => {
        const objectId = obj.id
        if (dbDoc.value!.data[objectId]) {
          // adding new comments should update
          if (JSON.stringify(obj) === JSON.stringify(dbDoc.value!.data[objectId]) && obj.type !== whiteboardConstants.objectTypes.discussion) {
            console.log('object already exists with same data', objectId, dbDoc.value!.data[objectId])
            return null
          }
          console.log('object already exists, updating', objectId, dbDoc.value!.data[objectId])
          return { p: [objectId], od: dbDoc.value!.data[objectId], oi: obj }
        }
        else {
          console.log('object does not exist, adding', obj)
          return { p: [objectId], oi: obj }
        }
      },
      ).filter(op => op !== null)
      if (ops.length > 0) {
        const batchSize = 100
        for (let i = 0; i < ops.length; i += batchSize) {
          const batch = ops.slice(i, i + batchSize)
          setTimeout(() => dbDoc.value!.submitOp(batch, {}, shareDBCallback), (i / batchSize) * 1000)
        }
        // dbDoc.value.submitOp(ops, {}, shareDBCallback)
      }
    }
  }

  function submitDeleteOp(obj: any, id?: string) {
    const objectId = id || obj.id
    if (dbDoc.value && dbDoc.value.data && obj && objectId && collaborationReady.value) {
      if (dbDoc.value.data[objectId]) {
        // console.log('object exists, deleting', objectId)
        dbDoc.value.submitOp([{ p: [objectId], od: obj }], {}, shareDBCallback)
      }
      else {
        console.warn('object does not exist, cannot delete', obj.id)
      }
    }
  }

  function shareDBCallback(error: any) {
    if (error) {
      console.error('ShareDB Error:', error)
    }
  }

  return { dbConn, dbDoc, userDoc, collaborationReady, connected }
}
